import { useState, useEffect } from 'react';

type UsePromiseWithRetryError = {
  data: undefined;
  success: false;
};

type UsePromiseWithRetrySuccess<T> = {
  data: T;
  success: true;
};

type UsePromiseWithRetryValue<T> = UsePromiseWithRetryError | UsePromiseWithRetrySuccess<T>;

type UsePromiseWithRetryReturn<T> =
  | (UsePromiseWithRetryValue<T> & { loading: false })
  | { data: undefined; loading: true; success: false };

const usePromiseWithRetry = <T>(
  promiseFactory: () => Promise<T>,
  retries = 1,
  deps: any[] = [],
): UsePromiseWithRetryReturn<T> => {
  const [state, setState] = useState<UsePromiseWithRetryReturn<T>>({
    data: undefined,
    loading: true,
    success: false,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      let retryCount = 0;
      let data: T | undefined = undefined;

      while (isMounted && retryCount < retries) {
        try {
          data = await promiseFactory();
          break;
        } catch {
          retryCount++;
        }
      }

      if (isMounted) {
        setState({
          data,
          loading: false,
          success: !!data,
        } as UsePromiseWithRetryReturn<T>);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps]);

  return state;
};

export default usePromiseWithRetry;
